/**
 * categories.js — the 20 library categories for the CPG Agreements & Legal Document Library.
 *
 * `id` is the stable key used by every document entry's `category` field and by the URL slug.
 * `filters` lists the founder-facing filter chips that map onto this category (a founder thinks
 * "co-packers", not "manufacturing & co-packing").
 */

export const CATEGORIES = [
  {
    id: "formation",
    slug: "formation",
    name: "Company Formation & Governance",
    short: "Formation",
    icon: "Building2",
    accent: "slate",
    blurb: "Entity documents, founder ownership, equity and board governance.",
    filters: ["Company Formation", "Founders", "Investors"],
    entityNote:
      "Documents differ sharply between an LLC and a corporation. LLCs use an Operating Agreement, membership interests and member consents; corporations use Bylaws, stock, shareholder agreements and board consents. Filter by entity type on each entry.",
  },
  {
    id: "confidentiality",
    slug: "confidentiality",
    name: "Confidentiality & Intellectual Property",
    short: "Confidentiality & IP",
    icon: "Lock",
    accent: "indigo",
    blurb: "NDAs, IP assignment, formula secrecy, artwork and likeness rights.",
    filters: ["Founders", "Product Development", "Manufacturing", "Marketing"],
  },
  {
    id: "product-development",
    slug: "product-development",
    name: "Product Development & Formulation",
    short: "Product Development",
    icon: "FlaskConical",
    accent: "violet",
    blurb: "Food scientists, flavor houses, labs, formula ownership and testing.",
    filters: ["Product Development"],
  },
  {
    id: "manufacturing",
    slug: "manufacturing",
    name: "Manufacturing & Co-Packing",
    short: "Manufacturing",
    icon: "Factory",
    accent: "amber",
    blurb: "Co-packers, contract manufacturing, quality agreements and recall responsibility.",
    filters: ["Manufacturing", "Co-Packers"],
  },
  {
    id: "supply",
    slug: "supply",
    name: "Ingredient, Packaging & Supplier Agreements",
    short: "Suppliers & Packaging",
    icon: "Package",
    accent: "orange",
    blurb: "Ingredients, cans, bottles, labels, cartons and master supply terms.",
    filters: ["Suppliers", "Packaging"],
  },
  {
    id: "distribution",
    slug: "distribution",
    name: "Distribution Agreements",
    short: "Distribution",
    icon: "Truck",
    accent: "blue",
    blurb: "DSD, wholesale, exclusive and territory distribution — the highest-stakes contract most CPG founders sign.",
    filters: ["Distribution"],
  },
  {
    id: "brokers",
    slug: "brokers",
    name: "Sales Brokers & Representatives",
    short: "Brokers & Reps",
    icon: "Handshake",
    accent: "cyan",
    blurb: "Retail and foodservice brokers, commission structures and house accounts.",
    filters: ["Brokers"],
  },
  {
    id: "retail",
    slug: "retail",
    name: "Retailer & Wholesale Agreements",
    short: "Retail",
    icon: "Store",
    accent: "teal",
    blurb: "Vendor agreements, consignment, slotting, demos and the deduction landscape.",
    filters: ["Retail"],
  },
  {
    id: "logistics",
    slug: "logistics",
    name: "Logistics, Warehousing & 3PL",
    short: "Logistics & 3PL",
    icon: "Warehouse",
    accent: "emerald",
    blurb: "3PLs, warehousing, freight, fulfillment and inventory accountability.",
    filters: ["3PL/Logistics"],
  },
  {
    id: "people",
    slug: "people",
    name: "Employees, Contractors & Consultants",
    short: "People",
    icon: "Users",
    accent: "rose",
    blurb: "Employment, contractors, consultants, commissions and separation.",
    filters: ["Employees"],
  },
  {
    id: "marketing",
    slug: "marketing",
    name: "Marketing, Creative & Digital",
    short: "Marketing & Creative",
    icon: "Megaphone",
    accent: "pink",
    blurb: "Agencies, designers, photographers, influencers and content ownership.",
    filters: ["Marketing", "Influencers"],
  },
  {
    id: "affiliate",
    slug: "affiliate",
    name: "Affiliate & Referral Agreements",
    short: "Affiliate & Referral",
    icon: "Share2",
    accent: "fuchsia",
    blurb: "Affiliates, referrals, ambassadors, tracking and FTC disclosure.",
    filters: ["Affiliates", "Influencers"],
  },
  {
    id: "licensing",
    slug: "licensing",
    name: "Licensing & Brand Partnerships",
    short: "Licensing",
    icon: "BadgeCheck",
    accent: "purple",
    blurb: "Trademark and celebrity licensing, co-branding and collaborations.",
    filters: ["Licensing"],
  },
  {
    id: "investors",
    slug: "investors",
    name: "Investors & Capital Raising",
    short: "Investors",
    icon: "TrendingUp",
    accent: "green",
    blurb: "SAFEs, convertible notes, priced rounds and investor governance.",
    filters: ["Investors", "Financing"],
    attorneyNote:
      "Securities documents. Selling equity or convertible instruments is regulated by federal and state securities law. Every document in this category should be prepared or reviewed by qualified counsel.",
  },
  {
    id: "financing",
    slug: "financing",
    name: "Loans & Business Financing",
    short: "Financing",
    icon: "Landmark",
    accent: "lime",
    blurb: "Loans, notes, lines of credit, equipment and inventory financing, guarantees.",
    filters: ["Financing"],
  },
  {
    id: "ecommerce",
    slug: "ecommerce",
    name: "E-Commerce & Website",
    short: "E-Commerce",
    icon: "Globe",
    accent: "sky",
    blurb: "Terms, privacy, subscriptions, SMS and email consent, disclosures.",
    filters: ["E-Commerce"],
    attorneyNote:
      "Privacy, auto-renewal and marketing-consent rules vary significantly by state and country and change often. Treat everything here as a starting outline for counsel, not a finished policy.",
  },
  {
    id: "events",
    slug: "events",
    name: "Events, Sampling & Promotions",
    short: "Events & Promos",
    icon: "PartyPopper",
    accent: "yellow",
    blurb: "Demos, sampling, sponsorships, contests and sweepstakes rules.",
    filters: ["Marketing", "Retail"],
  },
  {
    id: "international",
    slug: "international",
    name: "International Business",
    short: "International",
    icon: "Plane",
    accent: "stone",
    blurb: "Export, import, international distribution and cross-border licensing.",
    filters: ["International"],
    attorneyNote:
      "International agreements turn on the law of the counterparty's country, local agent-protection statutes, customs and import rules. Country-specific counsel is not optional here.",
  },
  {
    id: "disputes",
    slug: "disputes",
    name: "Termination, Disputes & Protection",
    short: "Disputes",
    icon: "ShieldAlert",
    accent: "red",
    blurb: "Termination, releases, demand letters, cure notices and cease-and-desist.",
    filters: ["Disputes"],
  },
  {
    id: "ma",
    slug: "ma",
    name: "Transactions, Acquisitions & Brand Sales",
    short: "M&A / Exit",
    icon: "Briefcase",
    accent: "zinc",
    blurb: "LOIs, asset and stock purchases, diligence, earn-outs and transitions.",
    filters: ["M&A / Exit"],
    attorneyNote:
      "These are attorney-led transaction documents. The entries here exist so a founder can follow the process and prepare — not so they can paper a sale themselves.",
  },
];

export const CATEGORY_BY_ID = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));
export const CATEGORY_BY_SLUG = Object.fromEntries(CATEGORIES.map((c) => [c.slug, c]));
export const CATEGORY_IDS = CATEGORIES.map((c) => c.id);

/** The founder-facing filter chips shown above the library. */
export const FILTERS = [
  "Company Formation",
  "Founders",
  "Investors",
  "Employees",
  "Product Development",
  "Manufacturing",
  "Co-Packers",
  "Suppliers",
  "Packaging",
  "Distribution",
  "Brokers",
  "Retail",
  "3PL/Logistics",
  "Marketing",
  "Influencers",
  "Affiliates",
  "Licensing",
  "E-Commerce",
  "Financing",
  "International",
  "Disputes",
  "M&A / Exit",
];

/** Tailwind classes per accent, mirroring the pattern used elsewhere in the platform. */
export const ACCENT_CLASSES = {
  slate: { text: "text-slate-600", bg: "bg-slate-50", ring: "ring-slate-200", border: "border-slate-500", dot: "bg-slate-500" },
  indigo: { text: "text-indigo-600", bg: "bg-indigo-50", ring: "ring-indigo-200", border: "border-indigo-500", dot: "bg-indigo-500" },
  violet: { text: "text-violet-600", bg: "bg-violet-50", ring: "ring-violet-200", border: "border-violet-500", dot: "bg-violet-500" },
  amber: { text: "text-pink-700", bg: "bg-pink-50", ring: "ring-pink-200", border: "border-pink-500", dot: "bg-pink-500" },
  orange: { text: "text-pink-700", bg: "bg-pink-50", ring: "ring-pink-200", border: "border-pink-500", dot: "bg-pink-500" },
  blue: { text: "text-blue-600", bg: "bg-blue-50", ring: "ring-blue-200", border: "border-blue-500", dot: "bg-blue-500" },
  cyan: { text: "text-cyan-700", bg: "bg-cyan-50", ring: "ring-cyan-200", border: "border-cyan-500", dot: "bg-cyan-500" },
  teal: { text: "text-teal-700", bg: "bg-teal-50", ring: "ring-teal-200", border: "border-teal-500", dot: "bg-teal-500" },
  emerald: { text: "text-emerald-700", bg: "bg-emerald-50", ring: "ring-emerald-200", border: "border-emerald-500", dot: "bg-emerald-500" },
  rose: { text: "text-rose-600", bg: "bg-rose-50", ring: "ring-rose-200", border: "border-rose-500", dot: "bg-rose-500" },
  pink: { text: "text-pink-600", bg: "bg-pink-50", ring: "ring-pink-200", border: "border-pink-500", dot: "bg-pink-500" },
  fuchsia: { text: "text-fuchsia-600", bg: "bg-fuchsia-50", ring: "ring-fuchsia-200", border: "border-fuchsia-500", dot: "bg-fuchsia-500" },
  purple: { text: "text-purple-600", bg: "bg-purple-50", ring: "ring-purple-200", border: "border-purple-500", dot: "bg-purple-500" },
  green: { text: "text-green-700", bg: "bg-green-50", ring: "ring-green-200", border: "border-green-500", dot: "bg-green-500" },
  lime: { text: "text-lime-700", bg: "bg-lime-50", ring: "ring-lime-200", border: "border-lime-500", dot: "bg-lime-500" },
  sky: { text: "text-sky-600", bg: "bg-sky-50", ring: "ring-sky-200", border: "border-sky-500", dot: "bg-sky-500" },
  yellow: { text: "text-yellow-700", bg: "bg-yellow-50", ring: "ring-yellow-200", border: "border-yellow-500", dot: "bg-yellow-500" },
  stone: { text: "text-stone-700", bg: "bg-stone-50", ring: "ring-stone-200", border: "border-stone-500", dot: "bg-stone-500" },
  red: { text: "text-red-600", bg: "bg-red-50", ring: "ring-red-200", border: "border-red-500", dot: "bg-red-500" },
  zinc: { text: "text-zinc-700", bg: "bg-zinc-50", ring: "ring-zinc-200", border: "border-zinc-500", dot: "bg-zinc-500" },
};
